import type maplibregl from 'maplibre-gl';
import type { RasterSourceSpecification } from 'maplibre-gl';
import type React from 'react';
import { useEffect, useState } from 'react';

import { useOptionalMapLibre } from './MapLibreContext';

interface OverzoomTileLayerProps { isCadastre: boolean }

interface TileSource { url: string }

export const OverzoomTileLayer: React.FC<OverzoomTileLayerProps> = ({ isCadastre }) => {
  const map = useOptionalMapLibre();
  const [orthoSourceIndex] = useState(0);
  const [planSourceIndex] = useState(0);
  const [cadastreSourceIndex] = useState(0);

  useEffect(() => {
    if (!map) { return; }
    const tileSources: Record<string, TileSource[]> = {
      ortho: [
        { url: 'https://data.geopf.fr/wmts?SERVICE=WMTS&VERSION=1.0.0&REQUEST=GetTile&STYLE=normal&TILEMATRIXSET=PM&FORMAT=image/jpeg&LAYER=ORTHOIMAGERY.ORTHOPHOTOS&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}' },
        { url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}' },
      ],
      plan: [
        { url: 'https://data.geopf.fr/wmts?SERVICE=WMTS&VERSION=1.0.0&REQUEST=GetTile&STYLE=normal&TILEMATRIXSET=PM&FORMAT=image/png&LAYER=GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}' },
        { url: 'https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png' },
      ],
      cadastre: [
        { url: 'https://data.geopf.fr/wmts?SERVICE=WMTS&VERSION=1.0.0&REQUEST=GetTile&STYLE=PCI%20vecteur&TILEMATRIXSET=PM&FORMAT=image/png&LAYER=CADASTRALPARCELS.PARCELLAIRE_EXPRESS&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}' },
      ],
    };

    const upsertRaster = (idBase: string, url: string) => {
      if (!map || !map.isStyleLoaded()) { return; }
      
      try {
        const sourceId = `${idBase}-source`;
        const layerId = `${idBase}-layer`;
        
        if (map.getLayer(layerId)) { map.removeLayer(layerId); }
        if (map.getSource(sourceId)) { map.removeSource(sourceId); }
        
        map.addSource(sourceId, { type: 'raster', tiles: [url], tileSize: 256 } as RasterSourceSpecification);
        const beforeId = map.getLayer('buildings-3d-threejs') ? 'buildings-3d-threejs' : undefined;
        
        if (beforeId) {
          map.addLayer({ id: layerId, type: 'raster', source: sourceId } as unknown as maplibregl.LayerSpecification, beforeId);
        } else {
          map.addLayer({ id: layerId, type: 'raster', source: sourceId } as unknown as maplibregl.LayerSpecification);
        }
      } catch (error) {
        console.warn('Error adding raster layer:', error);
      }
    };

    if (isCadastre) {
      upsertRaster('base', tileSources.plan[planSourceIndex].url);
    } else {
      upsertRaster('base', tileSources.ortho[orthoSourceIndex].url);
    }
    upsertRaster('cadastre', tileSources.cadastre[cadastreSourceIndex].url);

    return () => {
      if (!map || map.isStyleLoaded() === false) { return; }
      try {
        ['base-layer', 'cadastre-layer'].forEach((lid) => { if (map.getLayer(lid)) { map.removeLayer(lid); } });
        ['base-source', 'cadastre-source'].forEach((sid) => { if (map.getSource(sid)) { map.removeSource(sid); } });
      } catch (error) {
        // Ignore cleanup errors when map is being destroyed
        console.warn('Map cleanup error:', error);
      }
    };
  }, [map, isCadastre, orthoSourceIndex, planSourceIndex, cadastreSourceIndex]);

  return null;
};

export default OverzoomTileLayer;
